import React, { useRef } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import {
  FaMapMarkerAlt, FaChartLine, FaRegClock, FaCheckCircle,
} from 'react-icons/fa';
import AgentTrustChip from '../components/AgentTrustChip';

const TRUST_POINTS = [
  { icon: <FaChartLine size={13} />, label: 'Price trends' },
  { icon: <FaRegClock size={13} />, label: 'Days on market' },
  { icon: <FaCheckCircle size={13} />, label: '100% free' },
];

function SuburbSearchMobile({
  agentDetails, branding, selectedLocation, loadLocations,
  handleLocationChange, handleSubmit, formatGroupLabel, message, isSubmitting,
  onContactAgent,
}) {
  const { accentColor, buttonTextColor, isLight } = branding;

  // Ref to the search pill so the portaled dropdown can match its width/left
  // edge (react-select otherwise sizes the menu to the inner control, which
  // excludes the leading location icon).
  const pillRef = useRef(null);

  const isBranded =
    agentDetails &&
    agentDetails.landing_page_slug !== 'home-price' &&
    agentDetails.agent_name;

  // 16px input font keeps iOS from auto-zooming the page on focus.
  const selectStyles = {
    control: (p) => ({
      ...p, border: 'none', boxShadow: 'none', backgroundColor: 'transparent',
      minHeight: 50, cursor: 'text',
    }),
    valueContainer: (p) => ({ ...p, padding: '2px 4px' }),
    placeholder: (p) => ({ ...p, color: '#94a3b8', textAlign: 'left', fontSize: 16 }),
    input: (p) => ({ ...p, textAlign: 'left', color: '#0f172a', fontSize: 16 }),
    singleValue: (p) => ({ ...p, textAlign: 'left', fontWeight: 600, color: '#0f172a', fontSize: 16 }),
    menu: (p) => ({
      ...p, textAlign: 'left', borderRadius: 14, overflow: 'hidden',
      boxShadow: '0 12px 32px rgba(15,23,42,0.16)', marginTop: 8,
    }),
    menuPortal: (p) => {
      const rect = pillRef.current?.getBoundingClientRect();
      return {
        ...p,
        zIndex: 99999,
        ...(rect ? { left: rect.left, width: rect.width } : {}),
      };
    },
    option: (p, state) => ({
      ...p,
      backgroundColor: state.isFocused ? `${accentColor}12` : '#fff',
      color: '#0f172a', fontSize: '0.95rem', cursor: 'pointer',
      padding: '12px 14px',
      '&:active': { backgroundColor: `${accentColor}22` },
    }),
    dropdownIndicator: () => ({ display: 'none' }),
    indicatorSeparator: () => ({ display: 'none' }),
    loadingIndicator: (p) => ({ ...p, color: accentColor }),
  };

  return (
    <div
      className="psl-wrap psl-wrap--mobile"
      style={{
        '--psl-accent': accentColor,
        '--psl-accent-ring': `${accentColor}26`,
        '--psl-accent-soft': `${accentColor}14`,
      }}
    >
      <div className="psl-blob psl-blob--a" style={{ backgroundColor: accentColor }} aria-hidden="true" />
      <div className="psl-blob psl-blob--b" style={{ backgroundColor: accentColor }} aria-hidden="true" />

      <div className="psl-inner psl-inner--mobile">
        {!isBranded && (
          <AgentTrustChip
            agentDetails={agentDetails}
            accentColor={accentColor}
            size="sm"
            badgeLabel="Local market reports"
          />
        )}

        <h1 className="psl-title psl-title--mobile">
          What&rsquo;s happening in <span style={{ color: accentColor }}>your suburb?</span>
        </h1>

        <p className="psl-sub psl-sub--mobile">
          Search any suburb or postcode for a property trend report: prices, days on market, and recent sales.
        </p>

        <Form className="psl-searchcard" onSubmit={handleSubmit}>
          <div className="psl-search-pill psl-search-pill--mobile" ref={pillRef}>
            <span className="psl-search-icon"><FaMapMarkerAlt size={17} /></span>
            <div className="psl-search-select">
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadLocations}
                onChange={handleLocationChange}
                value={selectedLocation}
                noOptionsMessage={() => null}
                placeholder="Suburb or postcode…"
                openMenuOnClick={false}
                formatGroupLabel={formatGroupLabel}
                menuPortalTarget={document.body}
                styles={selectStyles}
                aria-label="Suburb or postcode"
              />
            </div>
          </div>

          <button
            type="submit"
            className="psl-btn psl-btn--block"
            disabled={isSubmitting}
            style={{
              backgroundColor: accentColor,
              color: buttonTextColor,
              border: isLight ? '1px solid #222' : 'none',
              boxShadow: `0 6px 18px ${accentColor}33`,
            }}
          >
            {isSubmitting ? <Spinner animation="border" size="sm" /> : 'View report'}
          </button>

          {message && <div className="psl-msg">{message}</div>}
        </Form>

        <div className="psl-trust psl-trust--mobile">
          {TRUST_POINTS.map((t, i) => (
            <span className="psl-trust-item" key={i}>{t.icon}{t.label}</span>
          ))}
        </div>

        {isBranded && (
          <div className="psl-agent psl-agent--mobile">
            {agentDetails.agent_profile_image && (
              <img
                className="psl-agent-photo"
                src={agentDetails.agent_profile_image}
                alt={agentDetails.agent_name}
              />
            )}
            <div className="psl-agent-info">
              <div className="psl-agent-name">{agentDetails.agent_name}</div>
              <div className="psl-agent-agency">{agentDetails.agency_name_short}</div>
            </div>
            <button
              type="button"
              className="psl-agent-btn"
              onClick={onContactAgent}
              style={{ color: accentColor }}
            >
              Contact {agentDetails.agent_name?.split(' ')[0] || 'us'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SuburbSearchMobile;
